import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, MessageCircle, Users, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { createBreadcrumbSchema } from "@/components/SEO";

const CommunityForum = () => {
  const [forumLink, setForumLink] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchForumLink();
  }, []);

  const fetchForumLink = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'community_forum_link')
        .maybeSingle();

      if (error) {
        console.error('Error fetching forum link:', error);
        setForumLink('https://discord.gg/6GYGwYrvdp'); // Default Discord link
      } else {
        setForumLink(data?.value || 'https://discord.gg/6GYGwYrvdp');
      }
    } catch (error) {
      console.error('Error:', error);
      setForumLink('https://discord.gg/6GYGwYrvdp');
    } finally {
      setLoading(false);
    }
  };

  const handleForumRedirect = () => {
    if (forumLink) {
      window.open(forumLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="Community Forum | Connect with AI Creators"
        description="Join our vibrant community of AI image creators. Share your work, get feedback, ask questions, and learn from fellow users in our Discord community."
        keywords="ai image community, bulk image generator forum, ai art community, creator network, discord ai art"
        url="/community"
        jsonLd={createBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Help Center', url: '/help' },
          { name: 'Community Forum', url: '/community' }
        ])}
      />
      <Header />
      
      <main className="pt-24">
        <div className="container mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Community Forum
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Community</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Connect with other users, share your creations, get help, and stay updated with the latest features and tips.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-8">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white mb-2">Community Forum</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-300 mb-6">
                  Join thousands of users in our active community forum where you can ask questions, 
                  share your AI-generated artwork, get feedback, and learn from experienced users.
                </p>
                <Button
                  onClick={handleForumRedirect}
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3"
                >
                  {loading ? (
                    'Loading...'
                  ) : (
                    <>
                      Join Community Forum
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <Users className="w-8 h-8 text-blue-400 mb-2" />
                  <CardTitle className="text-white">Connect</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    Meet fellow creators, make connections, and build lasting relationships in our welcoming community.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <MessageCircle className="w-8 h-8 text-purple-400 mb-2" />
                  <CardTitle className="text-white">Share & Learn</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    Share your artwork, get constructive feedback, and learn new techniques from experienced users.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardHeader>
                  <ArrowRight className="w-8 h-8 text-green-400 mb-2" />
                  <CardTitle className="text-white">Get Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300">
                    Get help with technical issues, troubleshooting, and learn best practices from the community.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CommunityForum;
